/* eslint-disable max-lines */
import type { ReactElement } from 'react';
import { useState, useRef, useContext, useEffect, MouseEvent } from 'react';
import type { FeatureCollection, GeoJsonProperties, LineString } from 'geojson';
import type { AnyProps, PointFeature, ClusterFeature } from 'supercluster';
import ReactMapGL, { FlyToInterpolator, Source, Layer, MapRef } from 'react-map-gl';
import { useMedia } from 'react-use';
import mapboxgl from 'mapbox-gl';
import { FormattedMessage } from 'react-intl';
import useSupercluster from '../../../hooks/useSupercluster';
import {
  StyledMapContainer,
  StyledLocationName,
  StyledNavigationControl,
  StyledMarker,
  StyledGlobeButton,
  StyledLocationDot,
  StyledClusterNumber,
  StyleLocationContainerWithFare,
} from './MapContainer.styles';
import AppContext from '../../../utils/createContext';
import { MediaQuery } from '../../../styles/mediaQuery';
import { Icon } from '../../atoms/Icon/Icon';
import type { AirportType, FlightType } from '../../../utils/types';
import useRouteLine from '../../../hooks/useRouteLine';
import { InformationContent } from '../../molecules/InformationContent/InformationContent';
import { ZoomLevels } from '../../../data/constants';
import { isConnected, isNonstop, isUnserved } from '../../../utils/checkFlightType';
import { theme } from '../../../styles/theme/default';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FallbackMap } from '../FallbackMap/FallbackMap';
import { ListViewButton } from '../../molecules/ListViewButton/ListViewButton';
import { useExternalBoolean, useExternalBooleanLabels } from '../../../hooks/useExternalValue';
import { FareComponent } from '../FareComponent/FareComponent';
// fix for transpiling issue, see https://github.com/mapbox/mapbox-gl-js/issues/10173
// prettier-ignore
// eslint-disable-next-line @typescript-eslint/no-var-requires,import/extensions,import/no-unresolved,import/no-webpack-loader-syntax, @typescript-eslint/no-explicit-any
(mapboxgl as any).workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

export function MapContainer(): ReactElement {
  const {
    viewport,
    setViewport,
    airportsData,
    points,
    setPoints,
    originAirport,
    setOriginAirport,
    destinationAirport,
    setDestinationAirport,
    setFlightType,
    nonstopChecked,
    setGoToHawaii,
    mapboxKey,
    mapboxStyleUrl,
    mapFailed,
    setMapFailed,
    setShowDisabledModal,
    setShowDisabledModalConnection,
  } = useContext(AppContext);
  const isDesktop = useMedia(MediaQuery.Large);
  const isTablet = useMedia(MediaQuery.Medium);
  const curvedLine = useRouteLine({ originAirport, destinationAirport });
  const [route, setRoute] = useState<FeatureCollection<LineString, GeoJsonProperties> | []>({
    type: 'FeatureCollection',
    features: [curvedLine],
  });
  const mapRef = useRef<MapRef>(null);

  const showNumbers = useExternalBoolean('showNumbers');
  const showLabels = useExternalBooleanLabels('showLabels');

  useEffect(() => {
    const airportPoints: Array<PointFeature<AnyProps>> = airportsData
      .filter((airport: AirportType) => {
        if (
          (originAirport.iata_code && airport.iata_code === originAirport.iata_code) ||
          (destinationAirport.iata_code && airport.iata_code === destinationAirport.iata_code)
        ) {
          return false;
        }
        return true;
      })
      .map((airport: AirportType) => ({
        type: 'Feature',
        properties: { cluster: false, airport, category: airport.federal_unit },
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(airport.longitude), parseFloat(airport.latitude)],
        },
      }));
    setPoints(airportPoints);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airportsData, originAirport, destinationAirport]);

  const bounds = mapRef.current ? mapRef.current.getMap().getBounds().toArray().flat() : null;

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom: viewport.zoom,
    options: { radius: 30, maxZoom: 20 }, // clusters-radius
  });

  const handleClusterClick = (cluster: PointFeature<AnyProps> | ClusterFeature<AnyProps>) => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const expansionZoom = Math.min(supercluster!.getClusterExpansionZoom(cluster.id as number), 20);
    setViewport({
      ...viewport,
      latitude,
      longitude,
      zoom: expansionZoom,
      transitionInterpolator: new FlyToInterpolator({ speed: 2 }),
      transitionDuration: 'auto',
    });
  };

  const handleClusterClickZoom = (cluster: PointFeature<AnyProps> | ClusterFeature<AnyProps>) => {
    const [longitude, latitude] = cluster.geometry.coordinates;

    setViewport({
      ...viewport,
      latitude,
      longitude,
      zoom: 8,
      transitionInterpolator: new FlyToInterpolator({ speed: 2 }),
      transitionDuration: 'auto',
    });
  };

  const handleAirportClick = (airport: AirportType | Record<string, never>, disabled: boolean) => {
    if (airport.suspended || (disabled && !nonstopChecked)) {
      setShowDisabledModal(true);
      return;
    }
    if (disabled && nonstopChecked) {
      setShowDisabledModalConnection(true);
      return;
    }
    if (airport.iata_code === originAirport.iata_code) {
      setOriginAirport({});
      setDestinationAirport({});
    } else if (airport.iata_code === destinationAirport.iata_code) {
      setDestinationAirport({});
    } else if (originAirport.airport_name) {
      setDestinationAirport(airport);
    } else {
      setOriginAirport(airport);
    }
  };

  const zoomOut = () => {
    setViewport({
      ...viewport,
      longitude: -97.9, // viewport-settings
      latitude: 36.3,
      zoom: isDesktop ? ZoomLevels.OutermostDesktop : ZoomLevels.OutermostMobile,
      transitionInterpolator: new FlyToInterpolator({ speed: 1 }),
      transitionDuration: 2200,
    });
  };

  useEffect(() => {
    setRoute({
      ...route,
      features: [curvedLine],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curvedLine]);

  useEffect(() => {
    let type: FlightType = 'none';
    if (originAirport.iata_code && destinationAirport.iata_code) {
      if (originAirport.nonstop.includes(destinationAirport.iata_code)) {
        type = 'nonstop';
      } else if (isConnected(originAirport, destinationAirport)) {
        type = 'connected';
      }
    }
    setFlightType(type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originAirport, destinationAirport]);

  // LFMA fare data - Method 1 hook
  // useEffect(() => {
  //   const script = document.createElement('script');
  //   script.src = "../../../assets/scripts/lfma.js";
  //   script.async = true;
  //   document.body.appendChild(script);
  //   return () => {
  //     document.body.removeChild(script);
  //   }
  // }, []);

  const departMessage = (
    <FormattedMessage
      id="map-info-depart-message"
      description="Fare component origin message"
      defaultMessage="Depart as low as*"
    />
  );
  const returnMessage = (
    <FormattedMessage
      id="map-info-return-message"
      description="Fare component origin message"
      defaultMessage="Depart as low as*"
    />
  );

  const clusterGroups = clusters.map((cluster) => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    const { cluster: isCluster, point_count: pointCount, airport } = cluster.properties;
    const connected = !isCluster ? isConnected(originAirport, airport) : undefined;
    const unservedAirport = !isCluster ? isUnserved(originAirport, airport) : undefined;
    const selected =
      !isCluster &&
      (airport.iata_code === originAirport.iata_code ||
        airport.iata_code === destinationAirport.iata_code);

    if (isCluster) {
      const majorHub = cluster.properties.airports.find(
        (currentAirport: { properties: { airport: AirportType } }) =>
          currentAirport.properties.airport.major_hub,
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let popularStation: any;
      if (viewport.zoom >= ZoomLevels.Middle) {
        popularStation = cluster.properties.airports.find(
          (currentAirport: { properties: { airport: AirportType } }) =>
            currentAirport.properties.airport.popular_station,
        );
      }

      const unservedCluster = cluster.properties.airports.map(
        (currentAirport: { properties: { airport: AirportType } }) => {
          return isUnserved(originAirport, currentAirport.properties.airport);
        },
      );
      const connectingCluster = cluster.properties.airports.map(
        (currentAirport: { properties: { airport: AirportType } }) => {
          return isConnected(originAirport, currentAirport.properties.airport);
        },
      );
      const nonstopCluster = cluster.properties.airports.map(
        (currentAirport: { properties: { airport: AirportType } }) => {
          return isNonstop(originAirport, currentAirport.properties.airport);
        },
      );

      const clusterLabel = () => {
        if (majorHub) {
          // eslint-disable-next-line no-nested-ternary
          return showNumbers && !showLabels ? (
            <StyledClusterNumber
              $isConnected={
                connectingCluster.includes(true) &&
                !nonstopCluster.includes(true) &&
                !unservedCluster.includes(true)
              }
              $isNumber
              className={
                // eslint-disable-next-line no-nested-ternary
                nonstopCluster.includes(true)
                  ? 'marker cluster-label-marker solid-blue'
                  : connectingCluster.includes(true) && !nonstopChecked
                  ? 'marker cluster-label-marker hollow-blue'
                  : 'marker cluster-label-marker solid-gray'
              }
            >
              {pointCount}
            </StyledClusterNumber>
          ) : // eslint-disable-next-line no-nested-ternary
          showLabels && !showNumbers ? (
            <StyledClusterNumber
              $isConnected={
                connectingCluster.includes(true) &&
                !nonstopCluster.includes(true) &&
                !unservedCluster.includes(true)
              }
              $isNumber={false}
              className={
                // eslint-disable-next-line no-nested-ternary
                nonstopCluster.includes(true)
                  ? 'marker cluster-label-marker solid-blue'
                  : connectingCluster.includes(true) && !nonstopChecked
                  ? 'marker cluster-label-marker hollow-blue'
                  : 'marker cluster-label-marker solid-gray'
              }
            >
              <span>{majorHub.properties.airport.city_served}</span>
            </StyledClusterNumber>
          ) : // eslint-disable-next-line no-nested-ternary
          showLabels && showNumbers ? (
            <StyledClusterNumber
              $isConnected={
                connectingCluster.includes(true) &&
                !nonstopCluster.includes(true) &&
                !unservedCluster.includes(true)
              }
              $isNumber
              className={
                // eslint-disable-next-line no-nested-ternary
                nonstopCluster.includes(true)
                  ? 'marker cluster-label-marker solid-blue'
                  : connectingCluster.includes(true) && !nonstopChecked
                  ? 'marker cluster-label-marker hollow-blue'
                  : 'marker cluster-label-marker solid-gray'
              }
            >
              <span>{majorHub.properties.airport.city_served}</span>
              {pointCount}
            </StyledClusterNumber>
          ) : !showLabels && !showNumbers ? (
            <StyledClusterNumber
              $isConnected={
                connectingCluster.includes(true) &&
                !nonstopCluster.includes(true) &&
                !unservedCluster.includes(true)
              }
              $isNumber={false}
              className={
                // eslint-disable-next-line no-nested-ternary
                nonstopCluster.includes(true)
                  ? 'marker cluster-label-marker solid-blue'
                  : connectingCluster.includes(true) && !nonstopChecked
                  ? 'marker cluster-label-marker hollow-blue'
                  : 'marker cluster-label-marker solid-gray'
              }
            ></StyledClusterNumber>
          ) : (
            <StyledClusterNumber
              $isConnected={
                connectingCluster.includes(true) &&
                !nonstopCluster.includes(true) &&
                !unservedCluster.includes(true)
              }
              $isNumber={false}
              className={
                // eslint-disable-next-line no-nested-ternary
                nonstopCluster.includes(true)
                  ? 'marker cluster-label-marker solid-blue'
                  : connectingCluster.includes(true) && !nonstopChecked
                  ? 'marker cluster-label-marker hollow-blue'
                  : 'marker cluster-label-marker solid-gray'
              }
            >
              {' '}
              <span>{majorHub.properties.airport.city_served}</span>
            </StyledClusterNumber>
          );
        }
        if (popularStation) {
          // eslint-disable-next-line no-nested-ternary
          return showNumbers && !showLabels ? (
            <StyledClusterNumber
              $isConnected={
                connectingCluster.includes(true) &&
                !nonstopCluster.includes(true) &&
                !unservedCluster.includes(true)
              }
              $isNumber
              className={
                // eslint-disable-next-line no-nested-ternary
                nonstopCluster.includes(true)
                  ? 'marker cluster-label-marker solid-blue'
                  : connectingCluster.includes(true) && !nonstopChecked
                  ? 'marker cluster-label-marker hollow-blue'
                  : 'marker cluster-label-marker solid-gray'
              }
            >
              {pointCount}
            </StyledClusterNumber>
          ) : // eslint-disable-next-line no-nested-ternary
          showLabels && !showNumbers ? (
            <StyledClusterNumber
              $isConnected={
                connectingCluster.includes(true) &&
                !nonstopCluster.includes(true) &&
                !unservedCluster.includes(true)
              }
              $isNumber={false}
              className={
                // eslint-disable-next-line no-nested-ternary
                nonstopCluster.includes(true)
                  ? 'marker cluster-label-marker solid-blue'
                  : connectingCluster.includes(true) && !nonstopChecked
                  ? 'marker cluster-label-marker hollow-blue'
                  : 'marker cluster-label-marker solid-gray'
              }
            >
              <span>{popularStation.properties.airport.city_served}</span>
            </StyledClusterNumber>
          ) : // eslint-disable-next-line no-nested-ternary
          showLabels && showNumbers ? (
            <StyledClusterNumber
              $isConnected={
                connectingCluster.includes(true) &&
                !nonstopCluster.includes(true) &&
                !unservedCluster.includes(true)
              }
              $isNumber
              className={
                // eslint-disable-next-line no-nested-ternary
                nonstopCluster.includes(true)
                  ? 'marker cluster-label-marker solid-blue'
                  : connectingCluster.includes(true) && !nonstopChecked
                  ? 'marker cluster-label-marker hollow-blue'
                  : 'marker cluster-label-marker solid-gray'
              }
            >
              <span>{popularStation.properties.airport.city_served}</span>
              {pointCount}
            </StyledClusterNumber>
          ) : !showLabels && !showNumbers ? (
            <StyledClusterNumber
              $isConnected={
                connectingCluster.includes(true) &&
                !nonstopCluster.includes(true) &&
                !unservedCluster.includes(true)
              }
              $isNumber={false}
              className={
                // eslint-disable-next-line no-nested-ternary
                nonstopCluster.includes(true)
                  ? 'marker cluster-label-marker solid-blue'
                  : connectingCluster.includes(true) && !nonstopChecked
                  ? 'marker cluster-label-marker hollow-blue'
                  : 'marker cluster-label-marker solid-gray'
              }
            ></StyledClusterNumber>
          ) : (
            <StyledClusterNumber
              $isConnected={
                connectingCluster.includes(true) &&
                !nonstopCluster.includes(true) &&
                !unservedCluster.includes(true)
              }
              $isNumber={false}
              className={
                // eslint-disable-next-line no-nested-ternary
                nonstopCluster.includes(true)
                  ? 'marker cluster-label-marker solid-blue'
                  : connectingCluster.includes(true) && !nonstopChecked
                  ? 'marker cluster-label-marker hollow-blue'
                  : 'marker cluster-label-marker solid-gray'
              }
            >
              {' '}
              <span>{popularStation.properties.airport.city_served}</span>
            </StyledClusterNumber>
          );
        }
        return showNumbers ? (
          <StyledClusterNumber
            $isNumber
            $isConnected={!nonstopCluster.includes(true) && connectingCluster.includes(true)}
            $isUnserved={
              unservedCluster.includes(true) &&
              !nonstopCluster.includes(true) &&
              !connectingCluster.includes(true)
            }
            $isNonstop={nonstopCluster.includes(true)}
            $nonStopOnly={nonstopChecked}
            className={
              // eslint-disable-next-line no-nested-ternary
              nonstopCluster.includes(true)
                ? 'marker cluster-label-marker solid-blue'
                : connectingCluster.includes(true) && !nonstopChecked
                ? 'marker cluster-label-marker hollow-blue'
                : 'marker cluster-label-marker solid-gray'
            }
          >
            {pointCount}
          </StyledClusterNumber>
        ) : (
          <StyledClusterNumber
            $isConnected={connectingCluster.includes(true) && !nonstopCluster.includes(true)}
            $isNumber={false}
            className={
              // eslint-disable-next-line no-nested-ternary
              nonstopCluster.includes(true)
                ? 'marker cluster-label-marker solid-blue'
                : connectingCluster.includes(true) && !nonstopChecked
                ? 'marker cluster-label-marker hollow-blue'
                : 'marker cluster-label-marker solid-gray'
            }
          ></StyledClusterNumber>
        );
      };

      return (
        <StyledMarker
          className="marker cluster-label-marker"
          key={cluster.id}
          longitude={longitude}
          latitude={latitude}
          $isCluster
        >
          <StyledLocationName
            type="button"
            className={
              unservedCluster.includes(false) || (nonstopChecked && !nonstopCluster.includes(true))
                ? `cluster-size-${pointCount}`
                : 'marker-is-disabled'
            } // unserved-cluster-styling
            $isActive={false}
            $isSuspended={false}
            $isCluster
            $isConnected={connectingCluster.includes(true) && !nonstopCluster.includes(true)}
            $isUnserved={
              (!nonstopCluster.includes(true) &&
                !connectingCluster.includes(true) &&
                unservedCluster.includes(true)) ||
              (!nonstopCluster.includes(true) && nonstopChecked)
            }
            $pointCount={pointCount}
            $isInSeasonalService={false}
            onClick={() =>
              pointCount === 2 ? handleClusterClickZoom(cluster) : handleClusterClick(cluster)
            }
            tabIndex={-1}
            $nonStopOnly={nonstopChecked}
          >
            {clusterLabel()}
          </StyledLocationName>
        </StyledMarker>
      );
    }

    const zoomLevelMiddleAndPopularStation =
      (isDesktop || (!isDesktop && !originAirport.iata_code) || !destinationAirport.iata_code) &&
      viewport.zoom >= ZoomLevels.Middle &&
      airport.popular_station;
    const zoomLevelInnermost =
      (isDesktop || (!isDesktop && !originAirport.iata_code) || !destinationAirport.iata_code) &&
      viewport.zoom >= ZoomLevels.Innermost;
    const selectedOnMobile =
      !isDesktop &&
      originAirport.iata_code &&
      destinationAirport.iata_code &&
      (airport.iata_code === originAirport.iata_code ||
        airport.iata_code === destinationAirport.iata_code);
    const mobileCluster =
      isCluster && !isDesktop && !originAirport.iata_code && !destinationAirport.iata_code;
    const isDisabled =
      unservedAirport || (nonstopChecked && connected) || airport.suspended || !airport.operational;

    if (
      (!isCluster && airport.major_hub) ||
      zoomLevelMiddleAndPopularStation ||
      zoomLevelInnermost ||
      selectedOnMobile ||
      mobileCluster
    ) {
      return (
        <StyledMarker
          className="marker location-label-marker"
          key={airport.iata_code}
          longitude={longitude}
          latitude={latitude}
          $isActive={selected}
          $isPopulair={airport.popular_station}
        >
          <StyledLocationName
            className={
              // eslint-disable-next-line no-nested-ternary
              isDisabled
                ? 'marker-is-disabled solid-grey'
                : connected
                ? 'hollow-blue'
                : 'solid-blue'
            }
            type="button"
            onClick={() => handleAirportClick(airport, isDisabled)}
            $isActive={selected}
            $isConnected={connected}
            $isSuspended={airport.suspended || !airport.operational}
            $isInSeasonalService={airport.seasonal_service}
            tabIndex={-1}
          >
            {airport.route_map_display_name}
            {airport.seasonal_service && <Icon.Umbrella />}
          </StyledLocationName>
        </StyledMarker>
      );
    }
    return (
      <StyledMarker
        className="marker location-dot-marker "
        key={airport.iata_code}
        longitude={longitude}
        latitude={latitude}
      >
        <StyledLocationDot
          // eslint-disable-next-line no-nested-ternary
          className={isDisabled ? 'solid-grey' : connected ? 'hollow-blue' : 'solid-blue'}
          onClick={() => handleAirportClick(airport, isDisabled)}
          $unavailable={unservedAirport || (nonstopChecked && connected) || isDisabled}
          $isSuspended={airport.suspended}
          $isInSeasonalService={airport.seasonal_service}
          $isUnserved={nonstopChecked}
          $isConnected={connected}
        >
          {airport.seasonal_service && <Icon.Umbrella />}
        </StyledLocationDot>
      </StyledMarker>
    );
  });

  return (
    <StyledMapContainer>
      {mapFailed ? (
        <FallbackMap />
      ) : (
        <>
          <ReactMapGL
            {...viewport}
            mapboxApiAccessToken={mapboxKey}
            width="100%"
            height="100%"
            onViewportChange={(event: MouseEvent) => {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              setViewport(event);
              setGoToHawaii(false);
            }}
            mapStyle={mapboxStyleUrl}
            maxZoom={10}
            minZoom={isDesktop ? ZoomLevels.OutermostDesktop : ZoomLevels.OutermostMobile}
            onError={() => {
              setMapFailed(true);
            }}
            attributionControl // map-attribution
            ref={mapRef}
          >
            {originAirport.longitude && destinationAirport.longitude && (
              <Source
                id="map-line"
                type="geojson"
                data={route as unknown as FeatureCollection<LineString, GeoJsonProperties>}
              >
                <Layer
                  id="route"
                  source="route"
                  type="line"
                  paint={
                    isConnected(originAirport, destinationAirport)
                      ? {
                          'line-width': 2,
                          'line-color': theme.color.blue,
                          'line-dasharray': [8, 2],
                        }
                      : {
                          'line-width': 2,
                          'line-color': theme.color.blue,
                        }
                  }
                />
              </Source>
            )}
            {clusterGroups}
            {originAirport.iata_code && (
              <StyledMarker
                className="marker active-label-marker"
                key={originAirport.iata_code}
                longitude={parseFloat(originAirport.longitude)}
                latitude={parseFloat(originAirport.latitude)}
                $isActive
              >
                <StyleLocationContainerWithFare>
                  <FareComponent
                    msg={departMessage}
                    origin={originAirport.iata_code}
                    destination={destinationAirport.iata_code}
                  />
                  <StyledLocationName
                    type="button"
                    onClick={() => handleAirportClick(originAirport, false)}
                    $isActive
                    tabIndex={-1}
                  >
                    {originAirport.route_map_display_name}
                  </StyledLocationName>
                </StyleLocationContainerWithFare>
              </StyledMarker>
            )}
            {destinationAirport.iata_code && (
              <StyledMarker
                className="marker active-label-marker"
                key={destinationAirport.iata_code}
                longitude={parseFloat(destinationAirport.longitude)}
                latitude={parseFloat(destinationAirport.latitude)}
                $isActive
              >
                <StyleLocationContainerWithFare>
                  <FareComponent
                    msg={returnMessage}
                    origin={destinationAirport.iata_code}
                    destination={originAirport.iata_code}
                  />
                  <StyledLocationName
                    type="button"
                    onClick={() => handleAirportClick(destinationAirport, false)}
                    $isActive
                    tabIndex={-1}
                  >
                    {destinationAirport.route_map_display_name}
                  </StyledLocationName>
                </StyleLocationContainerWithFare>
              </StyledMarker>
            )}
            {isTablet && (
              <StyledNavigationControl aria-label="zoom control buttons" showCompass={false} />
            )}
          </ReactMapGL>
          <InformationContent />
          <ListViewButton />
          <StyledGlobeButton aria-label="reset map zoom" onClick={zoomOut}>
            <Icon.Globe />
          </StyledGlobeButton>
        </>
      )}
    </StyledMapContainer>
  );
}
